import { ClientContractor, Truck, TruckDraft } from '@numeo/types-core'

/**
 * The one name a contractor is shown under.
 *
 * It was resolved four different ways across this page — company-first here, bare `name` there, `name || email`
 * in the row — so the same owner appeared under one name in the table, another in the asset drawer, and was
 * searchable under a third. Company first because that is what an owner IS on this surface: a carrier, not a
 * person. `email` is the last resort, since a contractor row can exist with neither name filled in.
 */
export const contractorDisplayName = (contractor: Pick<ClientContractor, 'name' | 'email' | 'companyDetails'>): string =>
    contractor.companyDetails?.name || contractor.name || contractor.email || 'Unnamed contractor'

interface TruckOwner {
    ownerId: string
    /** Absent when the id resolves to nothing — a deleted contractor, or a contractors fetch that failed. */
    contractor?: ClientContractor
}

/**
 * The contractor that OWNS a Box Truck vehicle, or undefined when there is no owner relation.
 *
 * Returning undefined for an owner id whose contractor is MISSING would report the vehicle as unowned, so
 * callers get the id back and decide — see `truckOwnerLabel` for the wording they all share.
 *
 * Takes a resolver rather than a Map so the two callers can use what each already holds (the table has a
 * `contractorsById` index built for exactly this; the picker has the array it renders). What must not be
 * duplicated is the RULE — which field is the owner, and that a missing contractor is still an owner — and
 * that lives here.
 */
export const resolveTruckOwner = (truck: Pick<Truck, 'boxTruck'>, findContractor: (id: string) => ClientContractor | undefined): TruckOwner | undefined => {
    const ownerId = truckOwnerId(truck)
    return ownerId ? { ownerId, contractor: findContractor(ownerId) } : undefined
}

/**
 * WHICH field holds a vehicle's owner, as a string id — or null when it has none.
 *
 * Trivial enough to inline, which is exactly why it was inlined in four places: the table, the picker, and
 * both arms of the save path. `ownerContractorId` is an ObjectId on the wire and a string in the UI, so every
 * copy also had to remember the `String(...)`, and one that forgets compares an object to an id and silently
 * never matches.
 */
export const truckOwnerId = (truck: Pick<TruckDraft, 'boxTruck'>): string | null =>
    truck.boxTruck?.ownerContractorId ? String(truck.boxTruck.ownerContractorId) : null

/**
 * The contractors the Box Truck owner endpoints will accept.
 *
 * Every one of them requires the owner profile, so a contractor without it can be offered, picked, and then
 * refused — with nowhere to be managed from even if it were not. Shared because two pickers now apply it: the
 * Assets table's owner column and the Add Truck form.
 */
export const eligibleBoxTruckOwners = (contractors: ClientContractor[]): ClientContractor[] => contractors.filter((contractor) => contractor.hasBoxTruckProfile)

/** What a vehicle's owner is called in the UI, or null when it has none. One definition, so the table, the
 *  picker and the read-only cell cannot disagree about an owner that could not be resolved. */
export const truckOwnerLabel = (owner: TruckOwner | undefined): string | null =>
    owner ? (owner.contractor ? contractorDisplayName(owner.contractor) : 'Unknown owner') : null
